import React, { useState } from 'react';
import {
  Box,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import api from '../../config/axios';

interface ISDOCDownloadProps {
  invoiceId: number;
  invoiceNumber: string;
  onDownloadComplete?: () => void;
}

const ISDOCDownload: React.FC<ISDOCDownloadProps> = ({
  invoiceId,
  invoiceNumber,
  onDownloadComplete
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });

  // Sanitize invoice number for filename (remove invalid characters)
  const sanitizeFilename = (filename: string): string => {
    return filename.replace(/[\/\\:*?"<>|]/g, '_').replace(/\s+/g, '_').trim();
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const response = await api.post(`/isdoc/${invoiceId}/download`, {}, {
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers
      const contentDisposition = response.headers['content-disposition'];
      const sanitizedInvoiceNumber = sanitizeFilename(invoiceNumber);
      let filename = `ISDOC_${sanitizedInvoiceNumber}_${new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-')}.isdoc`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setSnackbar({
        open: true,
        message: 'ISDOC soubor úspěšně stažen',
        severity: 'success'
      });

      if (onDownloadComplete) {
        onDownloadComplete();
      }
    } catch (error) {
      console.error('Error downloading ISDOC:', error);
      setSnackbar({
        open: true,
        message: 'Chyba při stahování ISDOC souboru',
        severity: 'error'
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title="Stáhnout ISDOC">
        <Button
          variant="contained"
          color="primary"
          onClick={handleDownload}
          disabled={isDownloading}
          startIcon={isDownloading ? <CircularProgress size={20} color="inherit" /> : <FileDownloadIcon />}
          size="small"
        >
          {isDownloading ? 'Stahuji...' : 'Stáhnout ISDOC'}
        </Button>
      </Tooltip>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ISDOCDownload;
